import { useParams, Link } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import SeoHead from '@/components/feature/SeoHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { novels } from '@/mocks/novels';
import type { Novel } from '@/mocks/novels';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const [novel, setNovel] = useState<Novel | null>(null);
  const [related, setRelated] = useState<Novel[]>([]);

  useEffect(() => {
    const found = novels.find((n) => n.id === Number(id));
    setNovel(found || null);

    if (found) {
      const relatedNovels = novels
        .filter((n) => n.id !== found.id && n.categorySlug === found.categorySlug)
        .slice(0, 3);
      setRelated(relatedNovels);
    }
  }, [id]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  if (!novel) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
              <i className="ri-file-search-line text-2xl text-foreground-300"></i>
            </div>
            <p className="text-foreground-500">未找到该小说</p>
            <Link to="/" className="mt-4 inline-block text-sm text-primary-500 hover:text-primary-600 cursor-pointer">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: novel.title,
    description: novel.description,
    author: { '@type': 'Person', name: novel.author },
    datePublished: '2026-01-01',
    dateModified: novel.updateDate,
    url: window.location.href,
    publisher: {
      '@type': 'Organization',
      name: '都市美艳后宫',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': window.location.href,
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `${novel.title}是什么类型的小说？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${novel.title}属于${novel.category}类型，是都市美艳后宫平台收录的精品都市爽文。`,
        },
      },
      {
        '@type': 'Question',
        name: `${novel.title}的作者是谁？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${novel.title}的作者是${novel.author}。`,
        },
      },
      {
        '@type': 'Question',
        name: `${novel.title}更新到什么程度了？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${novel.title}共有${novel.chapters}章，总计${novel.wordCount}，目前状态为${novel.status === 'completed' ? '已完结' : '连载中'}。`,
        },
      },
    ],
  };

  return (
    <>
      <SeoHead
        title={`${novel.title} - ${novel.author} | 都市美艳后宫`}
        description={`${novel.title}：${novel.description.substring(0, 120)}`}
        keywords={`${novel.title},${novel.category},都市爽文,${novel.tags.join(',')},都市美艳后宫`}
        canonicalUrl={window.location.href}
        ogType="article"
        articleData={{
          publishedTime: '2026-01-01',
          modifiedTime: novel.updateDate,
          author: novel.author,
        }}
        schema={{ ...articleSchema, ...faqSchema }}
      />
      <ScrollToTop />
      <div className="min-h-screen flex flex-col bg-background-50">
        <Navbar />
        <main className="flex-1 pt-16 md:pt-18">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: novel.category, href: `/category/${novel.categorySlug}` },
                { label: novel.title },
              ]}
            />

            <article className="px-4 md:px-6 py-6">
              <header className="mb-8">
                <div className="flex flex-col sm:flex-row gap-6">
                  <div className="w-[120px] h-[168px] md:w-[140px] md:h-[196px] flex-shrink-0 rounded-lg overflow-hidden mx-auto sm:mx-0">
                    <img
                      src={novel.coverImage}
                      alt={novel.title}
                      title={`${novel.title} - ${novel.author}`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>

                  <div className="flex-1 text-center sm:text-left">
                    <h1 className="font-heading text-xl md:text-2xl lg:text-3xl font-bold text-foreground-900 mb-2">
                      {novel.title}
                    </h1>
                    <p className="text-sm text-foreground-500 mb-3">
                      <span className="w-3 h-3 inline-flex items-center justify-center mr-0.5">
                        <i className="ri-user-line text-[10px]"></i>
                      </span>
                      {novel.author}
                    </p>

                    <div className="flex flex-wrap items-center justify-center sm:justify-start gap-3 mb-4">
                      <span className="inline-flex items-center gap-0.5 text-sm text-accent-500 font-semibold">
                        <span className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-star-fill text-xs"></i>
                        </span>
                        {novel.rating} 分
                      </span>
                      <span className="text-xs text-foreground-400">{novel.wordCount}</span>
                      <span className="text-xs text-foreground-400">共 {novel.chapters} 章</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        novel.status === 'completed'
                          ? 'bg-secondary-100 text-secondary-700'
                          : 'bg-accent-100 text-accent-700'
                      }`}>
                        {novel.status === 'completed' ? '已完结' : '连载中'}
                      </span>
                    </div>

                    <div className="flex flex-wrap gap-1.5 justify-center sm:justify-start mb-4">
                      {novel.tags.map((tag) => (
                        <span key={tag} className="text-[11px] px-2.5 py-1 rounded-full bg-secondary-50 text-secondary-700">
                          {tag}
                        </span>
                      ))}
                    </div>

                    <div className="flex items-center gap-4 justify-center sm:justify-start text-xs text-foreground-400">
                      <span>
                        <span className="w-3 h-3 inline-flex items-center justify-center mr-0.5">
                          <i className="ri-eye-line text-[10px]"></i>
                        </span>
                        {(novel.views / 10000).toFixed(0)}万 阅读
                      </span>
                      <span>
                        <span className="w-3 h-3 inline-flex items-center justify-center mr-0.5">
                          <i className="ri-heart-line text-[10px]"></i>
                        </span>
                        {(novel.favorites / 10000).toFixed(0)}万 收藏
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-6 p-4 md:p-5 bg-background-100 rounded-lg">
                  <h2 className="font-heading text-sm font-semibold text-foreground-800 mb-2">作品简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">{novel.description}</p>
                </div>
              </header>

              <section className="mt-8">
                <h2 className="font-heading text-lg font-semibold text-foreground-900 mb-4 flex items-center gap-2">
                  <span className="w-1 h-5 bg-primary-500 rounded-full"></span>
                  正文阅读
                </h2>
                <div
                  className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: novel.content }}
                />
              </section>

              <div className="flex items-center justify-between mt-10 pt-6 border-t border-background-200/70">
                <Link
                  to={`/category/${novel.categorySlug}`}
                  className="flex items-center gap-1 text-sm text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-left-line text-xs"></i>
                  </span>
                  返回{novel.category}分类
                </Link>

                <div className="flex items-center gap-3">
                  <button className="w-9 h-9 flex items-center justify-center rounded-full border border-background-200 text-foreground-500 hover:text-accent-500 hover:border-accent-300 transition-all cursor-pointer" aria-label="收藏">
                    <i className="ri-heart-line"></i>
                  </button>
                  <button className="w-9 h-9 flex items-center justify-center rounded-full border border-background-200 text-foreground-500 hover:text-primary-500 hover:border-primary-300 transition-all cursor-pointer" aria-label="分享">
                    <i className="ri-share-line"></i>
                  </button>
                </div>
              </div>

              {related.length > 0 && (
                <section className="mt-12 pt-8 border-t border-background-200/70">
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-5">
                    相关推荐
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {related.map((r) => (
                      <Link
                        key={r.id}
                        to={`/article/${r.id}`}
                        className="group flex items-center gap-3 p-3 rounded-lg border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
                      >
                        <div className="w-12 h-16 flex-shrink-0 rounded overflow-hidden">
                          <img src={r.coverImage} alt={r.title} className="w-full h-full object-cover object-top" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors truncate">
                            {r.title}
                          </p>
                          <p className="text-xs text-foreground-500 mt-0.5">{r.author}</p>
                          <span className="text-[11px] text-accent-500">
                            <span className="w-3 h-3 inline-flex items-center justify-center">
                              <i className="ri-star-fill text-[9px]"></i>
                            </span>
                            {r.rating}
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </section>
              )}
            </article>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}